<template>
  <VanPopup
    :show="show"
    @update:show="emit('update:show', $event)"
    class="h-[80%]"
    position="bottom"
  >
    <VanTabs v-model:active="activeTab">
      <VanTab :title="$t(`Chats.chatScene.edit.scoreType1`)">
        <div
          v-for="character in characterList"
          class="px-4 py-2 mx-4 my-2 flex justify-between gap-3 bg-base-200"
        >
          <div class="w-9 overflow-hidden">
            <UserAvatar
              :avatar-url="
                character.role === 2
                  ? mateStore.currentMate?.mediaUrl
                  : character.mate?.mediaUrl
              "
              class="w-9"
            />
            <p class="text-xs text-base-content/50 mt-1 max-w-9">
              {{
                character.role === 2
                  ? mateStore.currentMate?.name
                  : character.mate?.name
              }}
            </p>
          </div>
          <div class="flex-1">
            <div v-if="!character.scorezz?.length" class="text-base-content/50">
              {{ $t("Chats.chatScene.edit.noScore") }}
            </div>
            <template v-else>
              <template
                v-if="
                  (character.scorezz?.filter(
                    (v) => v.type === ChatSceneScoreType.Attribute
                  ).length ?? 0) > 0
                "
              >
                <div class="flex justify-between flex-1 text-base-content/50">
                  <div>{{ $t("Chats.chatScene.edit.attr") }}</div>
                  <div>{{ $t("Chats.chatScene.edit.value") }}</div>
                </div>
                <div
                  :class="['flex justify-between flex-1 mb-1', attr.status]"
                  v-for="attr in character.scorezz?.filter(
                    (v) => v.type === ChatSceneScoreType.Attribute
                  )"
                >
                  <div>{{ attr.name }}</div>
                  <div>
                    {{ attr.currentScore ?? attr.defaultScore ?? 0 }}
                  </div>
                </div>
              </template>
              <template
                v-if="
                  (character.scorezz?.filter(
                    (v) => v.type === ChatSceneScoreType.Status
                  ).length ?? 0) > 0
                "
              >
                <div
                  class="flex justify-between flex-1 text-base-content/50 mt-2"
                >
                  <div>{{ $t("Chats.chatScene.edit.scoreType1") }}</div>
                  <div>{{ $t("Chats.chatScene.edit.value") }}</div>
                </div>
                <div
                  :class="['flex justify-between flex-1 mb-1', status.status]"
                  v-for="status in character.scorezz?.filter(
                    (v) => v.type === ChatSceneScoreType.Status
                  )"
                >
                  <div>{{ status.name }}</div>
                  <div class="flex items-center gap-1">
                    {{ status.currentScore ?? status.defaultScore ?? 0 }}

                    <i
                      v-if="status.changeScore"
                      :class="[
                        'iconify',
                        status.status,
                        (status.changeScore && status.changeScore) > 0
                          ? 'mingcute--large-arrow-up-fill'
                          : 'mingcute--large-arrow-down-fill',
                      ]"
                    />

                    <i
                      v-if="status.changeReason"
                      class="iconify mingcute--information-line text-base-content/50"
                      v-tooltip="status.changeReason"
                    />
                  </div>
                </div>
              </template>
            </template>
          </div>
        </div>
      </VanTab>
      <VanTab :title="$t('Chats.chatScene.edit.target')">
        <!-- <div class="p-4">{{ $t("Chats.chatScene.edit.scoreType0") }}</div> -->

        <VanCellGroup class="pt-2" inset>
          <ChatTargetItem
            v-for="character in characterList"
            :character="character"
            :chat-scene="props.mateSession.chatScene!"
          />
        </VanCellGroup>
      </VanTab>
    </VanTabs>
  </VanPopup>
</template>

<script lang="ts" setup>
import { ChatSceneScoreType, ChatSceneTargetType } from "@/enums/chatEnums";
import { useMate } from "@/stores/mate";
import {
  ChatSceneCharacter,
  ChatSceneCharacterTarget,
  ChatSceneScoreStatusUpdate,
} from "@/types/chat";
import { MateSession } from "@/types/entity";
import { onMounted, ref, watch } from "vue";

const props = defineProps<{
  show: boolean;
  mateSession: MateSession;
}>();

const mateStore = useMate();

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
}>();

const activeTab = ref(0);
const characterList = ref<ChatSceneCharacter[]>([]);
const player = ref<ChatSceneCharacter>();
const winTarget = ref<ChatSceneCharacterTarget>();
const losingTarget = ref<ChatSceneCharacterTarget>();

// 更新显示状态
const updateStatusView = () => {
  const { currentCharacterScores } = props.mateSession.chatSceneStatus || {};
  const characterScoreMap: Record<
    number,
    Record<number, Partial<ChatSceneScoreStatusUpdate>>
  > = {};
  if (currentCharacterScores && currentCharacterScores.length > 0) {
    for (const {
      scoreId,
      characterId,
      changeScore,
      value,
      changeReason,
    } of currentCharacterScores) {
      if (!characterScoreMap[characterId]) characterScoreMap[characterId] = {};

      characterScoreMap[characterId][scoreId] = {
        changeScore,
        value,
        changeReason,
      };
    }
  }

  for (const character of characterList.value) {
    character.scorezz?.forEach((score) => {
      // 找到变化的值
      if (characterScoreMap[character.id!]?.[score.id!]) {
        const { changeScore, changeReason, value } =
          characterScoreMap[character.id!][score.id!];
        score.currentScore = value;
        // 如果是赢的目标
        if (
          winTarget.value?.targetCharacterId === character.id &&
          winTarget.value?.scoreId === score.id
        ) {
          if (!changeScore) {
            score.status = undefined;
          } else if (
            ((winTarget.value?.targetOperator === ">" ||
              winTarget.value?.targetOperator === ">=") &&
              changeScore > 0) ||
            ((winTarget.value?.targetOperator === "<" ||
              winTarget.value?.targetOperator === "<=") &&
              changeScore < 0)
          ) {
            score.status = "text-success";
          } else if (
            ((winTarget.value?.targetOperator === ">" ||
              winTarget.value?.targetOperator === ">=") &&
              changeScore < 0) ||
            ((winTarget.value?.targetOperator === "<" ||
              winTarget.value?.targetOperator === "<=") &&
              changeScore > 0)
          ) {
            score.status = "text-error";
          }
        }

        // 如果是输的目标
        if (
          losingTarget.value?.targetCharacterId === character.id &&
          losingTarget.value?.scoreId === score.id
        ) {
          if (!changeScore) {
            score.status = undefined;
          } else if (
            ((losingTarget.value?.targetOperator === ">" ||
              losingTarget.value?.targetOperator === ">=") &&
              changeScore > 0) ||
            ((losingTarget.value?.targetOperator === "<" ||
              losingTarget.value?.targetOperator === "<=") &&
              changeScore < 0)
          ) {
            score.status = "text-error";
          } else if (
            ((losingTarget.value?.targetOperator === ">" ||
              losingTarget.value?.targetOperator === ">=") &&
              changeScore < 0) ||
            ((losingTarget.value?.targetOperator === "<" ||
              losingTarget.value?.targetOperator === "<=") &&
              changeScore > 0)
          ) {
            score.status = "text-success";
          }
        }
        score.changeScore = changeScore;
        score.changeReason = changeReason;
      }
    });
  }
};

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      characterList.value = props.mateSession.chatScene?.characterzz ?? [];
      updateStatusView();
    }
  }
);

onMounted(() => {
  characterList.value = props.mateSession.chatScene?.characterzz ?? [];
  player.value = characterList.value.find((v) => v.role === 2);
  player.value?.targetzz?.forEach((v) => {
    if (v.type === ChatSceneTargetType.WINNING) {
      winTarget.value = v;
    }
    if (v.type === ChatSceneTargetType.LOSING) {
      losingTarget.value = v;
    }
  });
  updateStatusView();
});
</script>
